import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import * as fromRoot from 'app/core/store';
import { BookingState } from 'app/core/store/reducers/booking.reducer';
import { AuthService } from 'app/shared/authentication/services/auth.service';
import { Identifier } from 'app/shared/storage/services/local-storage.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'core-auth-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  @Output() loginEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeModalEmitter: EventEmitter<any> = new EventEmitter<any>();
  forgotPasswordFormGroup?: UntypedFormGroup;
  verificationCodeFormGroup?: UntypedFormGroup;
  isEmailSent = false;
  subscription: Subscription = new Subscription();
  isSubmitted = false;

  constructor(
    private store: Store<fromRoot.State>,
    private authService: AuthService,
    private ref: ChangeDetectorRef,
    private translateService: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fillFormGroup();
    this.handleBookingContext();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fillFormGroup(): void {
    this.forgotPasswordFormGroup = new UntypedFormGroup({
      email: new UntypedFormControl(null, [
        Validators.required,
        Validators.email,
        this.validateNoPlus.bind(this),
      ]),
      navigationBackUri: new UntypedFormControl(
        `${window.location.origin}/reset-password`,
        Validators.required
      ),
      portalType: new UntypedFormControl(2, Validators.required),
      otherData: new UntypedFormControl(null),
    });
    this.ref.markForCheck();
  }

  fillVerificationFormGroup(): void {
    this.verificationCodeFormGroup = new UntypedFormGroup({
      additionalInformation: new UntypedFormControl(
        this.forgotPasswordFormGroup?.get('email')?.value,
        [
          Validators.required,
          Validators.email,
          Validators.pattern(
            /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i
          ),
        ]
      ),
      verificationCode: new UntypedFormControl(null, Validators.required),
    });
    this.ref.markForCheck();
  }

  onForgotPasswordSubmit(): void {
    this.isSubmitted = true;
    if (this.forgotPasswordFormGroup?.valid) {
      this.subscription.add(
        this.authService.forgotPassword(this.forgotPasswordFormGroup?.value).subscribe(() => {
          this.fillVerificationFormGroup();
          this.isSubmitted = false;
          this.isEmailSent = true;
          this.ref.markForCheck();
        })
      );
    }
  }

  onVerificationCodeSubmit(): void {
    this.isSubmitted = true;
    const verificationCode = this.verificationCodeFormGroup?.get('verificationCode')?.value;
    const additionalInformation =
      this.verificationCodeFormGroup?.get('additionalInformation')?.value;
    if (this.verificationCodeFormGroup?.valid) {
      this.subscription.add(
        this.authService
          .getNonceWithVerificationCode(verificationCode, additionalInformation)
          .subscribe(
            nonce => {
              this.router
                .navigate([decodeURIComponent('/reset-password')], {
                  queryParams: { state: nonce.id, userName: nonce.data.userName },
                })
                .then();
              this.closeModalEmitter.emit();
              this.ref.markForCheck();
            },
            () => {
              this.ref.markForCheck();
            }
          )
      );
    }
  }

  displayRequiredError(control: AbstractControl): boolean {
    return control.hasError('required') && (control.touched || this.isSubmitted);
  }

  validateNoPlus(control: UntypedFormControl): { [key: string]: any } | null {
    return !(control.value as string)?.includes('+')
      ? null
      : {
          validateNoPlus: this.translateService.instant('validation.notAllowPlusSignInEmail'),
        };
  }

  displayPatternError(control: AbstractControl): boolean {
    return control?.hasError('validateNoPlus');
  }

  onBack(): void {
    if (this.isEmailSent) {
      this.isEmailSent = false;
      return;
    }
    this.loginEmitter.emit();
  }

  private handleBookingContext(): void {
    const regExpServices = new RegExp('^/book-online/booking-reasons/\\d*/services/\\d*');
    const regExpPractitioners = new RegExp('^/book-online/practitioners/\\d*/booking-reasons/\\d*');
    this.subscription.add(
      this.store.pipe(select(fromRoot.getBookingState)).subscribe(bookingState => {
        if (bookingState) {
          const state = JSON.parse(JSON.stringify(bookingState));
          if (
            regExpServices.test(this.router.routerState.snapshot.url) ||
            regExpPractitioners.test(this.router.routerState.snapshot.url)
          ) {
            state.isBooking = true;
          }
          this.forgotPasswordFormGroup?.get('otherData')?.setValue(this.createOtherData(state));
        }
        this.ref.markForCheck();
      })
    );
  }

  private createOtherData(state: BookingState): any {
    return {
      bookingAppSession: localStorage.getItem(Identifier.TYPE_SOURCE_SESSION_TOKEN),
      practitionerId: state.practitionerId,
      serviceId: state.serviceId,
      startDateTime: state.startDateTime,
      caseId: state.caseId,
      appointmentBookingMode: state.appointmentBookingMode,
      bookingReasonId: state.bookingReasonId,
      treatmentPlanId: state.treatmentPlanId,
      integrationId: state.integrationId,
      partnerToken: state.partnerToken,
      isBooking: state.isBooking,
      sysAppointmentDeliveryMethodId: state.sysAppointmentDeliveryMethodId,
      isForWaitingList: state.isForWaitingList,
    };
  }
}
